<template>
  <v-data-table 
    :headers="headers" 
    item-key="JobID" 
    :items="items.results" 
    :loading="loading" 
    :server-items-length="items.totalResults" 
    :options.sync="options" 
    show-expand 
    :expanded.sync="expanded" 
    :footer-props="{ 'items-per-page-options': [5, 10, 15, 20, -1] }">
    <template slot="body.prepend"> 
      <v-snackbar v-model="downloadingReport" elevation="0">
        Your file is being downloaded
      </v-snackbar>
      <v-snackbar v-model="errorDownloadReport" elevation="0">
        {{reportError}}
      </v-snackbar>
    </template>
    <template #:body="{items}">
      <tbody v-if="items.length > 0">
        <tr v-for="item in items" :key="item.JobID">
          <td>{{`${item.JobReference}`}}</td>
          <td>{{`${item.State}`}}</td>
          <td>{{`${item.Location}`}}</td>
          <td>{{`${item.StartDate}`}}</td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td :colspan="headers.length" style="text-align: center">No Jobs</td>
        </tr>
      </tbody>
    </template>
    <template #[`item.data-table-expand`]="{ isExpanded, expand }">
      <v-btn icon elevation="1" @click="expand(true)" v-if="isExpanded == false">
        <v-icon>mdi-chevron-down</v-icon>
      </v-btn>
      <v-btn icon elevation="1" @click="expand(false)" v-if="isExpanded">
        <v-icon>mdi-chevron-up</v-icon>
      </v-btn>
    </template>
    <template #expanded-item="{ item }">
      <td colspan="2">
        <div class="ma-4">
          <h4>Address</h4>
          <p>{{item.Address == null ? "-" : item.Address}}</p>
        </div>
        <div class="ma-4">
          <h4>Start Time</h4>
          <p>{{item.StartTime == null ? "-" : item.StartTime}}</p>
        </div>
        <div class="ma-4">
          <h4>Estimated Duration (Days)</h4>
          <p>{{item.EstimatedDays == null ? "-" : item.EstimatedDays}}</p>
        </div>
      </td>
      <td colspan="2">
        <div class="ma-4">
          <h4>Contact(s)</h4>
          <span style="white-space: pre-line">{{item.ContactInformation == null ? "-" : item.ContactInformation}}</span>
        </div>
        <div class="ma-4">
          <h4>Details Wrong?</h4>
          <a href="https://servicefm.com.au/contact/" target="_blank">Contact Us</a>
        </div>
      </td>
      <td colspan="2">
        <div class="ma-4">
          <h4>Reports</h4>
          <v-list v-if="item.Reports.length > 0" dense style="background: #f5f5f5;">
            <v-list-item v-for="report in item.Reports" :key="report.ReportID">
              <v-list-item-icon>
                <v-icon>mdi-circle-small</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <a @click="downloadReport(report)">{{report.ReportType}}</a>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <p v-else>No Reports available</p>
        </div>
      </td>
      <td colspan="1">
        <div class="ma-4">
          <router-link :to="`/reports?jobRef=${item.JobReference}`">View all Reports</router-link>
        </div>
      </td>
    </template>
    <template #[`item.JobStatus`]="{item}">
      <v-chip :style="GetColourAsCSSAttribute(item.JobStatusColourHex)">{{`${item.JobStatus}`}}</v-chip>
    </template>
    <template #[`item.StartDate`]="{item}">
      <td>{{`${$dateHelper.GetDateAsDMYString(item.StartDate)}`}}</td>
    </template>
    <template #[`item.FinalisedDate`]="{item}">
      <td>{{`${$dateHelper.GetDateTimeAsDMYHMString(item.FinalisedDate)}`}}</td>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as JobAPI from '@/api/jobAPI';
import PaginatedResults from '../../../../shared/models/paginatedResults';
import * as UserAPI from '@/api/userAPI';
import User from '../../../../shared/models/user';
import axios from 'axios';
import Report from '../../../../shared/models/report';
import * as reportAPI from '@/api/reportAPI';
import { vxm } from '@/store';

@Component({})
export default class JobList extends Vue {
  @Prop(String) public jobReference!: string | null;
  @Prop(Number) public stateID!: number | null;
  @Prop(String) public location!: string | null;
  @Prop() public statusIDs!: number[] | null;
  @Prop(String) public startDateFrom!: string | null;
  @Prop(String) public startDateTo!: string | null;
  @Prop(String) public finalisedDateFrom!: string | null;
  @Prop(String) public finalisedDateTo!: string | null;

  @Watch('options', { immediate: true, deep: true })
  onOptionsChanged(value: any, oldValue: any) {
    if(oldValue != undefined) {
      this.load();
    }
  }
  @Watch('selectedClient', { immediate: true, deep: true})
  onSelectedClientChanged(){
      this.load();
  }

  get selectedClient() { return  vxm.AuthStore.targetClient; }

  private get headers() {
    return [
      {
        text: 'Job Ref.',
        value: 'JobReference',
        sortable: true,
      },
      {
        text: 'State',
        value: 'State',
        sortable: true,
      },
      {
        text: 'Location',
        value: 'Location',
        sortable: true,
      },
      {
        text: 'Job Status',
        value: 'JobStatus',
        sortable: true,
      },
      {
        text: 'Start Date',
        value: 'StartDate',
        sortable: true,
      },
      {
        text: 'Finalised Date',
        value: 'FinalisedDate',
        sortable: true,
      },
    ];
  }

  private loading = false;
  private loadError = "";
  private options = { sortBy:[], sortDesc:[], page: 1, itemsPerPage: 20 };
  private expanded = [];
  private loggedInUser: User | null = null;

  private downloadingReport = false;
  private errorDownloadReport = false;
  private reportError: string | null = null;

  private items: PaginatedResults = {
    totalResults: 0,
    results: []
  };

  public async load() {
    this.loadError = "";
    this.loading = true;

    //Race condition with state ID change between parent and child components
    await this.$nextTick();

    const { sortBy, sortDesc, page, itemsPerPage } = this.options;
    this.loggedInUser = await UserAPI.getLoggedInUser();

    this.items = {
      totalResults: 0,
      results: []
    };

    try {
      this.items = await JobAPI.getJobs(page
        , itemsPerPage
        , sortBy.length === 1 ? sortBy[0] : null
        , sortDesc.length === 1 ? sortDesc[0] : null
        , this.jobReference
        , this.stateID
        , this.location
        , this.statusIDs
        , this.startDateFrom
        , this.startDateTo
        , this.finalisedDateFrom
        , this.finalisedDateTo);
    } catch (e) {
      this.loadError = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private GetColourAsCSSAttribute(colourHex: string): string {
    return `background: ${colourHex}; color: white;`;
  }

  private async downloadReport(report: Report): Promise<void> {
    this.downloadingReport = true;

    try {
      await reportAPI.downloadReport(report.ReportID);
    }
    catch(e) {
      this.downloadingReport = false;
      
      this.errorDownloadReport = true;
      this.reportError = (e as Error).message

      // eslint-disable-next-line no-console
      console.log('in component catch', e)
    }
    finally {
      this.downloadingReport = false;
    }
  }
}
</script>

<style>
.details{
 margin-top: 10px;
 margin-bottom: 10px;
}
</style>
